import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth.ts'
import { getAvailableLoginProviders } from '../services/auth.ts'
import {
  AUTH_PROVIDER_COLORS,
  AUTH_UI,
  type AuthProvider,
} from '../config/auth.ts'
import { t } from '../i18n/translations.ts'

type RecoveryConfirm = {
  provider: AuthProvider
  idToken: string
}

function providerLabel(provider: AuthProvider): string {
  if (provider === 'apple') return t('appleLogin')
  if (provider === 'google') return t('googleLogin')
  return 'LINE'
}

interface Props {
  open: boolean
  onClose: () => void
  onLoggedIn?: () => void
  /** モーダルを開いた理由（「オンライン対戦を始めるには〜」等）。 */
  reason?: string
  /** 期限切れ session の復旧時は /auth/link ではなく /auth/recover を使う。 */
  mode?: 'sign-in' | 'recover'
}

/**
 * ログインモーダル。
 *
 * プラットフォームに応じてボタンを出し分ける:
 *   - iOS  → Apple + Google
 *   - Android → Google
 *   - LINE Mini App → LINE
 *   - Web  → Google
 *
 * 失敗時は Retry / Cancel 選択を表示する（ymg-sdk-guide.md の UI 推奨パターン）。
 * キャンセル時はモーダルを閉じる（ゲストのままを許可）。
 */
export function AuthLoginModal({
  open,
  onClose,
  onLoggedIn,
  reason,
  mode = 'sign-in',
}: Props) {
  const {
    signInGoogle,
    signInApple,
    signInLINE,
    reauthenticate,
    confirmRecovery,
    lastError,
    clearError,
    isSignedIn,
  } = useAuth()
  const [busyProvider, setBusyProvider] = useState<AuthProvider | null>(null)
  const [showErrorRetry, setShowErrorRetry] = useState(false)
  const [pendingRecovery, setPendingRecovery] = useState<RecoveryConfirm | null>(null)

  // ログイン済みになったら onLoggedIn を呼んで閉じる
  useEffect(() => {
    if (!open) return
    if (mode === 'recover') return
    if (isSignedIn) {
      onLoggedIn?.()
      onClose()
    }
  }, [open, isSignedIn, mode, onLoggedIn, onClose])

  // モーダルを閉じるときにエラー状態をリセット
  useEffect(() => {
    if (!open) {
      setBusyProvider(null)
      setShowErrorRetry(false)
      setPendingRecovery(null)
      clearError()
    }
  }, [open, clearError])

  // 背景スクロール抑止
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // ESC で閉じる
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (pendingRecovery) return
      if (e.key === 'Escape' && !busyProvider) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busyProvider, onClose, pendingRecovery])

  // プラットフォーム別に利用可能なプロバイダ一覧（services/auth に集約）。
  // SettingsModal AccountTab と同じヘルパーを参照することで表示の整合性を保つ。
  const availableProviders = useMemo<AuthProvider[]>(
    () => getAvailableLoginProviders(),
    [],
  )

  if (!open) return null

  const doSignIn = async (provider: AuthProvider) => {
    setBusyProvider(provider)
    setShowErrorRetry(false)
    let result
    if (mode === 'recover') {
      result = await reauthenticate(provider)
    } else if (provider === 'google') {
      result = await signInGoogle()
    } else if (provider === 'apple') {
      result = await signInApple()
    } else {
      result = await signInLINE()
    }
    setBusyProvider(null)
    if (mode === 'recover' && result.ok) {
      onLoggedIn?.()
      onClose()
      return
    }
    if (!result.ok && result.reason === 'needs_recovery_confirm') {
      setPendingRecovery({ provider: result.provider, idToken: result.idToken })
      return
    }
    if (!result.ok && result.reason === 'error') {
      setShowErrorRetry(true)
    }
  }

  const handleConfirmRecovery = async () => {
    if (!pendingRecovery) return
    setBusyProvider(pendingRecovery.provider)
    setShowErrorRetry(false)
    const result = await confirmRecovery(pendingRecovery.provider, pendingRecovery.idToken)
    setBusyProvider(null)
    if (result.ok) {
      setPendingRecovery(null)
      return
    }
    if (result.reason === 'error') {
      setPendingRecovery(null)
      setShowErrorRetry(true)
    }
  }

  return (
    <div
      className="auth-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={() => {
        if (!busyProvider && !pendingRecovery) onClose()
      }}
    >
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <header className="auth-modal__header">
          <h2 id="auth-modal-title" className="auth-modal__title">
            {t('signInTitle')}
          </h2>
          <button
            type="button"
            className="auth-modal__close"
            aria-label={t('closeAria')}
            disabled={!!busyProvider}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="auth-modal__body">
          {reason && <p className="auth-modal__reason">{reason}</p>}

          {showErrorRetry && lastError && (
            <div className="auth-modal__error" role="alert">
              <p>{AUTH_UI.errorMessage}</p>
              <p className="auth-modal__error-detail">{lastError}</p>
              <div className="auth-modal__error-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    setShowErrorRetry(false)
                    clearError()
                  }}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          <div className="auth-modal__providers">
            {availableProviders.map((p) => (
              <button
                key={p}
                type="button"
                className="auth-modal__provider"
                disabled={!!busyProvider}
                style={{
                  backgroundColor: AUTH_PROVIDER_COLORS[p].bg,
                  color: AUTH_PROVIDER_COLORS[p].fg,
                }}
                onClick={() => {
                  void doSignIn(p)
                }}
              >
                {busyProvider === p ? t('signInBusy') : providerLabel(p)}
              </button>
            ))}
          </div>

          {mode === 'sign-in' && (
            <p className="auth-modal__note">
              {t('signInCancelNote')}
            </p>
          )}
        </div>

        {pendingRecovery && (
          <div className="auth-modal__error" role="alert">
            <p>{t('recoverConfirmTitle')}</p>
            <p className="auth-modal__error-detail">{t('recoverConfirmMessage')}</p>
            <div className="auth-modal__error-actions">
              <button
                type="button"
                className="btn btn--secondary"
                disabled={!!busyProvider}
                onClick={() => setPendingRecovery(null)}
              >
                {t('recoverConfirmCancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!!busyProvider}
                onClick={() => {
                  void handleConfirmRecovery()
                }}
              >
                {busyProvider ? t('processing') : t('recoverConfirmContinue')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
